import { useLocation, useNavigate } from 'react-router-dom';
import { useState, useRef, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import {
  type BadgeState,
  type NavItem,
  getNavPath,
  USERS_MAIN_NAV_ITEMS,
  getMoreMenuItemsForUser,
  getArtistMainNavItems,
  isMainNavItemActive,
} from '@/lib/navigationItems';
import { useUserData } from '@/hooks/useUserData';
import { useUnreadChatsCount } from '@/hooks/useUnreadChatsCount';
import { useNotificationSettingsListener } from '@/hooks/useNotificationSettingsListener';
import { isIPad, isNative } from '@/lib/platform';
import { isCurrentUserAdmin } from '@/hooks/useAdminMode';
import { useAuthState } from '@/hooks/useAuth';
import { isAdminUserEmail } from '@/components/admin/AdminAuthGuard';
import { useBookingSystem } from '@/contexts/BookingSystemContext';
import { useAnalytics } from '@/hooks/useAnalytics';
import { getAnalyticsDeviceContext } from '@/analytics/analyticsConfig';

export function TopNav() {
  const location = useLocation();
  const navigate = useNavigate();
  const { t } = useTranslation();
  const [isMoreOpen, setIsMoreOpen] = useState(false);
  const moreRef = useRef<HTMLDivElement>(null);
  const { isArtist, artistId, loading: userDataLoading, getCachedProfile } = useUserData();
  const cachedProfile = getCachedProfile();
  const effectiveArtistId = artistId ?? cachedProfile?.artistId ?? null;
  const showArtistNav = userDataLoading
    ? (cachedProfile?.isArtist ?? isArtist)
    : isArtist;
  const { user } = useAuthState();
  const { hasUnread: hasUnreadChats, count: unreadCount } = useUnreadChatsCount();
  const { needsSetup: needsNotificationSetup } = useNotificationSettingsListener();
  const isAdmin = isCurrentUserAdmin();
  const { hasBookingSystem, subscription } = useBookingSystem();
  const { trackEvent } = useAnalytics();

  // Build badge state
  const badgeState: BadgeState = {
    hasUnreadChats,
    unreadCount,
    needsNotificationSetup,
    isNative: isNative(),
    currentPath: location.pathname,
  };

  // Close dropdown when clicking outside
  useEffect(() => {
    function handleClickOutside(event: MouseEvent) {
      if (moreRef.current && !moreRef.current.contains(event.target as Node)) {
        setIsMoreOpen(false);
      }
    }

    if (isMoreOpen) {
      document.addEventListener('mousedown', handleClickOutside);
      return () => document.removeEventListener('mousedown', handleClickOutside);
    }
  }, [isMoreOpen]);

  const isTrial = subscription?.status === 'trial';
  const mainNavItems: readonly NavItem[] = showArtistNav
    ? getArtistMainNavItems({ hasBookingSystem: !!hasBookingSystem, isTrial: !!isTrial })
    : USERS_MAIN_NAV_ITEMS;
  const moreMenuItems: readonly NavItem[] = getMoreMenuItemsForUser({
    isArtist: showArtistNav,
    isAdmin: isAdminUserEmail(user?.email),
  });

  // Check if current path is active (top bar)
  const isActiveMain = (item: NavItem) =>
    isMainNavItemActive(item, location.pathname, effectiveArtistId);

  // "More" items use path matches only
  const isActiveMore = (item: NavItem) =>
    item.pathMatches.some(
      (match) => location.pathname === match || location.pathname.startsWith(`${match}/`)
    );

  const handleNavigate = (item: NavItem, navType: 'top' | 'more') => {
    const { device, platform } = getAnalyticsDeviceContext();
    trackEvent('nav_click', {
      nav_type: navType,
      item_key: item.id,
      device,
      platform
    });
    const path = getNavPath(item, effectiveArtistId);
    
    // Redirect admins to admin live chat page
    if (item.id === 'liveChat' && isAdmin) {
      navigate('/live-chat-admin');
      setIsMoreOpen(false);
      return;
    }

    navigate(path);
    setIsMoreOpen(false);
  };

  // List of paths where we don't want to show the top nav
  const hiddenPaths = [
    '/privacy-policy',
    '/cookie-policy',
    '/artist-signup',
    '/onboarding',
    '/onboarding-admin',
    '/live-chat-admin',
    '/fotw-uploads',
    '/board-post-drafts',
    '/chats/new',
    '/giftcard',
    '/giftcard/success',
    '/events/add',
    '/deals/add',
    '/book',
  ];

  const pathSegments = location.pathname.split('/').filter(Boolean);
  const isDynamicArtistPath = pathSegments.length === 1 && 
    !['discover', 'feed', 'board', 'pulse', 'artists', 'events', 'swipe', 'chat', 'chats', 'admin', 'profile', 'settings', 'artist', 'booking-system', 'guest-artists', 'fotw', 'giftcard', 'rate-my-artist', 'live-chat', 'tattoo-artist-matcher'].includes(pathSegments[0]);

  const isEventEditPath = location.pathname.match(/^\/events\/[^/]+\/edit\/?$/);
  const isDealEditPath = location.pathname.match(/^\/deals\/[^/]+\/edit\/?$/);
  
  // Hide top nav for ALL booking system routes (dashboard, clients, settings, etc.)
  const isBookingSystemPath = location.pathname.startsWith('/booking-system/') || location.pathname === '/booking-system';

  const shouldHideNav = 
    hiddenPaths.some(path => location.pathname === path || location.pathname.startsWith(`${path}/`)) ||
    !!isEventEditPath ||
    !!isDealEditPath ||
    (location.pathname.startsWith('/chats/') && location.pathname !== '/chats') ||
    isDynamicArtistPath ||
    isBookingSystemPath;

  if (shouldHideNav) {
    return null;
  }

  return (
    <nav
      className={`${isIPad() ? 'hidden' : 'hidden md:block'} sticky top-0 z-50 border-b border-border/50 bg-background/95 backdrop-blur-md md:mb-[var(--desktop-top-nav-content-gap)] dark:border-border/30`}
    >
      <div className="phone-container-padded pb-[var(--desktop-top-nav-inner-padding-y)] pt-[var(--desktop-top-nav-inner-padding-y)]" ref={moreRef}>
        <div className="flex items-center gap-6">
          {mainNavItems.map((item) => {
            const navItem = item as NavItem;
            const active = isActiveMain(navItem);
            const badge = (navItem as any).getBadge?.(badgeState);
            
            return (
              <button
                key={item.id}
                onClick={() => handleNavigate(navItem, 'top')}
                className={`text-sm relative ${
                  active 
                    ? 'text-foreground' 
                    : 'text-foreground/60 hover:text-foreground'
                }`}
              >
                {t(navItem.labelKey)}
                {badge?.show && badge.count && (
                  <span className="absolute -top-1 -right-2 text-[10px] text-foreground/60">
                    {badge.count}
                  </span>
                )}
              </button>
            );
          })}
          
          <div>
            <button
              onClick={() => {
                const nextState = !isMoreOpen;
                setIsMoreOpen(nextState);
                if (nextState) {
                  const { device, platform } = getAnalyticsDeviceContext();
                  trackEvent('nav_more_opened', { device, platform });
                }
              }}
              className="text-sm text-foreground/60 hover:text-foreground"
            >
              more
            </button>
          </div>
        </div>
        
        {isMoreOpen && (
          <div className="mt-3 bg-background">
            {moreMenuItems.map((item) => {
              const navItem = item as NavItem;
              const active = isActiveMore(navItem);
              const badge = (navItem as any).getBadge?.(badgeState);
              
              return (
                <button
                  key={item.id}
                  onClick={() => handleNavigate(navItem, 'more')}
                  className={`w-full text-left py-2 text-sm flex items-center ${
                    active 
                      ? 'text-foreground' 
                      : 'text-foreground/60 hover:text-foreground/80'
                  }`}
                >
                  <span className="mr-2">{'>'}</span>
                  <span className="relative">
                    {t(navItem.labelKey)}
                    {badge?.show && (badge.dot || badge.count) && (
                      <span className="absolute -top-1 -right-2">
                        {badge.dot && !badge.count && (
                          <span className="w-1.5 h-1.5 bg-foreground/60 rounded-full inline-block" />
                        )}
                        {badge.count && (
                          <span className="text-[10px] text-foreground/60">
                            {badge.count}
                          </span>
                        )}
                      </span>
                    )}
                  </span>
                </button>
              );
            })}
          </div>
        )}
      </div>
    </nav>
  );
}
